import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Play, ExternalLink, MapPin, AlertTriangle } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import useScrollReveal from '@/hooks/useScrollReveal';
import AcknowledgmentModal from '@/components/AcknowledgmentModal';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeroSection from '@/components/HeroSection';
import AboutSection from '@/components/AboutSection';
import BrandWatermark from '@/components/BrandWatermark';
import BookSessionCta from '@/components/BookSessionCta';
import AmbientParticles from '@/components/AmbientParticles';
import HowItWorksSection from '@/components/HowItWorksSection';
import RatesSection from '@/components/RatesSection';
import BoundariesSection from '@/components/BoundariesSection';
import BookingSection from '@/components/BookingSection';
import FAQSection from '@/components/FAQSection';
import ContactActionButtons from '@/components/ContactActionButtons';

export function ClientLanding() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  // Check if user already acknowledged
  const hasAcknowledged = localStorage.getItem('casa_client_acknowledgment');
  
  // New flow: animation -> acknowledgment -> content
  // If already acknowledged, skip to content
  const [stage, setStage] = useState(hasAcknowledged ? 'content' : 'animation');
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);

  useEffect(() => {
    if (stage === 'content') {
      api.trackEvent('page_view', 'client_landing', '/client');
    }
  }, [stage]);

  // Apple-style scroll reveal on content sections (re-runs once we hit content)
  useScrollReveal([stage]);

  // Function to handle animation completion - moved above useEffect
  const handleAnimationComplete = () => {
    setAnimationComplete(true);
    setTimeout(() => {
      setStage('acknowledgment');
    }, 500);
  };

  // Handle animation playback
  useEffect(() => {
    if (stage === 'animation' && videoRef.current) {
      const attemptPlay = async () => {
        try {
          await videoRef.current.play();
        } catch (err) {
          console.log('Autoplay prevented');
          setShowPlayButton(true);
        }
      };
      
      if (videoRef.current.readyState >= 3) {
        attemptPlay();
      } else {
        videoRef.current.addEventListener('canplay', attemptPlay, { once: true });
      }
      
      // Timeout fallback
      const timeout = setTimeout(() => {
        if (stage === 'animation') {
          handleAnimationComplete();
        }
      }, 8000);
      
      return () => clearTimeout(timeout);
    }
  }, [stage]);

  const handleManualPlay = async () => {
    setShowPlayButton(false);
    if (videoRef.current) {
      try {
        await videoRef.current.play();
      } catch (err) {
        handleAnimationComplete();
      }
    }
  };

  const handleAcknowledgmentAccept = () => {
    setStage('content');
  };

  const handleAcknowledgmentDecline = () => {
    navigate('/');
  };

  const locationContent = {
    en: {
      title: 'Service Area',
      subtitle: 'Where We Are',
      description: 'Near Mile 15 and Texas Blvd, Weslaco, TX',
      note: 'Exact location details are shared after confirmation to maintain privacy and discretion.',
      viewMap: 'View on Map',
      cancellation: 'Cancellation Policy',
      cancellationText: 'Please provide at least 2 hours notice for any scheduling changes. Deposits for confirmed appointments are non-refundable for no-shows or last-minute cancellations.',
      termsLink: 'Full Terms of Service',
    },
    es: {
      title: 'Área de Servicio',
      subtitle: 'Dónde Estamos',
      description: 'Near Mile 15 and Texas Blvd, Weslaco, TX',
      note: 'Los detalles exactos de la ubicación se comparten después de la confirmación para mantener la privacidad y discreción.',
      viewMap: 'Ver en Mapa',
      cancellation: 'Política de Cancelación',
      cancellationText: 'Por favor proporciona al menos 2 horas de aviso para cualquier cambio de programación. Los depósitos para citas confirmadas no son reembolsables por ausencias o cancelaciones de último momento.',
      termsLink: 'Términos de Servicio Completos',
    }
  };

  const lt = locationContent[language];

  // Show animation FIRST when coming from hub
  if (stage === 'animation') {
    return (
      <div 
        className={`fixed inset-0 z-50 bg-[#07060B] flex items-center justify-center overflow-hidden transition-opacity duration-500 ${
          animationComplete ? 'opacity-0' : 'opacity-100'
        }`}
        data-testid="client-animation"
      >
        <video
          ref={videoRef}
          src={siteConfig.animations.page}
          className="w-full h-full object-contain max-w-5xl"
          playsInline
          muted
          preload="auto"
          onEnded={handleAnimationComplete}
          onError={handleAnimationComplete}
        />
        
        {showPlayButton && (
          <button
            onClick={handleManualPlay}
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#07060B]/90 cursor-pointer group"
            data-testid="play-animation-btn"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF4DA6] to-[#8B5CFF] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4DA6]/30">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
            <p className="mt-4 text-[#A7A3BA] text-sm">Tap to play</p>
          </button>
        )}
        
        {!showPlayButton && (
          <button
            onClick={handleAnimationComplete}
            className="absolute bottom-8 right-8 px-4 py-2 text-sm text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors opacity-0 animate-[fadeIn_0.5s_ease-out_2s_forwards]"
          >
            Skip →
          </button>
        )}
      </div>
    );
  }

  // Show acknowledgment after animation
  if (stage === 'acknowledgment') {
    return (
      <>
        <Helmet>
          <title>{siteConfig.seo.client.title}</title>
        </Helmet>
        <div className="min-h-screen bg-[#07060B]">
          <AcknowledgmentModal
            type="client"
            storageKey="casa_client_acknowledgment"
            onAccept={handleAcknowledgmentAccept}
            onDecline={handleAcknowledgmentDecline}
          />
        </div>
      </>
    );
  }

  // Show main content
  return (
    <>
      <Helmet>
        <title>{siteConfig.seo.client.title}</title>
        <meta name="description" content={siteConfig.seo.client.description} />
        <meta property="og:title" content={siteConfig.seo.client.title} />
        <meta property="og:description" content={siteConfig.seo.client.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://${siteConfig.brand.domain}/client`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={siteConfig.seo.client.title} />
        <meta name="twitter:description" content={siteConfig.seo.client.description} />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen bg-[#07060B] flex flex-col animate-[fadeIn_0.8s_ease-out] relative overflow-x-hidden" data-testid="client-landing-page">
        <Navbar />

        {/* Softly drifting ambient particles — anchored to the viewport so the
            depth follows the scroll without ever stealing focus from content. */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <AmbientParticles count={32} testid="client-ambient-particles" />
        </div>

        {/* Scroll-revealed floating brand watermark — fades in once the user
            scrolls past the About/media block. Sits behind all content. */}
        <BrandWatermark triggerSelector="#about" />

        {/* Persistent "Book Session Now" CTA — bottom-center, hides when
            booking section is in view. */}
        <BookSessionCta targetId="book" />

        <main className="flex-1 pt-20 relative z-10">
          <HeroSection />
          
          {/* Scroll indicator */}
          <div className="flex justify-center -mt-8 mb-8 animate-bounce">
            <ChevronDown className="w-8 h-8 text-[#FF4DA6]/50" />
          </div>
          
          <div className="neon-divider" />
          
          {/* Pricing FIRST - before What We Offer */}
          <div data-reveal>
            <RatesSection />
          </div>
          
          <div className="neon-divider" />
          
          {/* What We Offer */}
          <div data-reveal>
            <AboutSection />
          </div>
          
          {/* Simple & Discreet - How It Works (formatted like What We Offer) */}
          <div data-reveal>
            <HowItWorksSection />
          </div>
          
          <div className="neon-divider" />
          
          <div data-reveal>
            <BoundariesSection />
          </div>
          
          <div className="neon-divider" />
          
          <div data-reveal>
            <BookingSection />
          </div>
          
          <div data-reveal>
            <FAQSection />
          </div>
          
          {/* Location / Service Area Section */}
          <section id="location" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="location-section" data-reveal>
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#27D7FF] text-sm font-semibold uppercase tracking-widest mb-2">
                  {lt.subtitle}
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF]">
                  {lt.title}
                </h2>
              </div>

              <div className="neon-card text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-[#27D7FF]/20 flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-8 h-8 text-[#27D7FF]" />
                </div>
                <p className="text-[#F4F2FF] text-xl font-semibold mb-2">
                  {lt.description}
                </p>
                <p className="text-[#A7A3BA] text-sm mb-6">
                  {lt.note}
                </p>
                
                {siteConfig.location.mapsLink && (
                  <a
                    href={siteConfig.location.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-neon btn-neon-cyan inline-flex"
                    data-testid="location-map-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{lt.viewMap}</span>
                  </a>
                )}
              </div>

              {/* Cancellation Policy + ToS */}
              <div className="neon-card border-[#FF4DA6]/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#FF4DA6]/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-[#FF4DA6]" />
                  </div>
                  <h3 className="font-heading font-semibold text-[#F4F2FF]">
                    {lt.cancellation}
                  </h3>
                </div>
                <p className="text-[#A7A3BA] text-sm leading-relaxed">
                  {lt.cancellationText}
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />

        {/* Note: the canonical persistent CTA on this surface is
            <BookSessionCta /> mounted near the top of this component.
            The legacy `mobile-book-cta` sticky bar was removed so the
            two fixed CTAs no longer collide on mobile. */}
      </div>
    </>
  );
}

export default ClientLanding;
